// src/components/Common/ImportPatientsDialog.jsx
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileUpload as UploadIcon,
  FileDownload as TemplateIcon,
  CheckCircle as ValidIcon,
  ErrorOutline as ErrorIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { patientService } from '../../services/ris';

/**
 * Robust CSV Line Parser that respects quoted values with commas
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"' || char === "'") {
      if (inQuotes && line[i + 1] === char) {
        current += char;
        i++; // skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

export default function ImportPatientsDialog({
  open,
  onClose,
  onImportComplete,
  isDark = true,
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parsingError, setParsingError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  const resetState = () => {
    setFile(null);
    setParsedRows([]);
    setParsingError('');
    setIsImporting(false);
    setImportProgress(0);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (isImporting) return;
    resetState();
    onClose();
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'Patient ID',
      'First Name',
      'Last Name',
      'Gender (M/F/O)',
      'Date of Birth (YYYY-MM-DD)',
      'Phone',
      'Email',
      'National ID',
      'Address'
    ];
    const sampleRows = [
      ['PT-10001', 'John', 'Doe', 'M', '1985-04-12', '+1 555-0199', 'john.doe@example.com', 'NID-99281', '123 Main St, Springfield'],
      ['PT-10002', 'Jane', 'Smith', 'F', '1992-08-25', '+1 555-0244', 'jane.smith@example.com', 'NID-44812', '456 Elm Ave, Metropolis']
    ];
    const csv = '\uFEFF' + [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'patient_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParsingError('');
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        if (!text || typeof text !== 'string') {
          setParsingError('The uploaded file appears to be empty.');
          return;
        }

        const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
        if (lines.length < 2) {
          setParsingError('CSV must include a header row and at least one patient record.');
          return;
        }

        const rawHeaders = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
        
        // Map header indices
        const colMap = {
          mrn: rawHeaders.findIndex(h => h.includes('mrn') || h.includes('patientid') || h.includes('id')),
          firstName: rawHeaders.findIndex(h => h.includes('first') || h.includes('fname')),
          lastName: rawHeaders.findIndex(h => h.includes('last') || h.includes('lname') || h.includes('surname')),
          fullName: rawHeaders.findIndex(h => h.includes('name') && !h.includes('first') && !h.includes('last')),
          gender: rawHeaders.findIndex(h => h.includes('gender') || h.includes('sex')),
          dob: rawHeaders.findIndex(h => h.includes('birth') || h.includes('dob') || h.includes('dateofbirth')),
          phone: rawHeaders.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('tel')),
          email: rawHeaders.findIndex(h => h.includes('email') || h.includes('mail')),
          nationalId: rawHeaders.findIndex(h => h.includes('national') || h.includes('ssn') || h.includes('nin')),
          address: rawHeaders.findIndex(h => h.includes('address') || h.includes('city') || h.includes('location')),
        };

        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = parseCSVLine(lines[i]);
          if (cols.length === 0 || cols.every(c => c === '')) continue;

          let firstName = colMap.firstName !== -1 ? cols[colMap.firstName] : '';
          let lastName = colMap.lastName !== -1 ? cols[colMap.lastName] : '';
          
          if (!firstName && colMap.fullName !== -1 && cols[colMap.fullName]) {
            const parts = cols[colMap.fullName].split(' ');
            firstName = parts[0] || '';
            lastName = parts.slice(1).join(' ') || '';
          }

          let gender = colMap.gender !== -1 ? (cols[colMap.gender] || '').toUpperCase() : 'U';
          if (gender.startsWith('M')) gender = 'M';
          else if (gender.startsWith('F')) gender = 'F';
          else if (gender.startsWith('O')) gender = 'O';
          else gender = 'U';

          const mrn = colMap.mrn !== -1 ? cols[colMap.mrn] : '';
          const dob = colMap.dob !== -1 ? cols[colMap.dob] : '';
          const phone = colMap.phone !== -1 ? cols[colMap.phone] : '';
          const email = colMap.email !== -1 ? cols[colMap.email] : '';
          const nationalId = colMap.nationalId !== -1 ? cols[colMap.nationalId] : '';
          const address = colMap.address !== -1 ? cols[colMap.address] : '';

          const errors = [];
          if (!firstName && !lastName) errors.push('Name is required');

          rows.push({
            index: i,
            mrn,
            firstName,
            lastName,
            gender,
            birthDate: dob,
            phone,
            email,
            nationalId,
            address,
            isValid: errors.length === 0,
            errors,
          });
        }

        if (rows.length === 0) {
          setParsingError('No valid rows found in file.');
          return;
        }

        setParsedRows(rows);
      } catch (err) {
        console.error('CSV parse error:', err);
        setParsingError('Failed to parse CSV file. Please verify format.');
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleImport = async () => {
    const validRows = parsedRows.filter(r => r.isValid);
    if (validRows.length === 0) {
      alert('No valid rows to import.');
      return;
    }

    setIsImporting(true);
    setImportProgress(0);

    let successCount = 0;
    let failCount = 0;
    const failures = [];

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        const payload = {
          patientId: row.mrn || undefined,
          mrn: row.mrn || undefined,
          firstName: row.firstName,
          lastName: row.lastName,
          name: [row.firstName, row.lastName].filter(Boolean).join(' '),
          patientName: [row.firstName, row.lastName].filter(Boolean).join(' '),
          sex: row.gender,
          patientSex: row.gender,
          birthDate: row.birthDate || undefined,
          patientBirthDate: row.birthDate || undefined,
          phone: row.phone,
          email: row.email,
          nationalId: row.nationalId,
          address: row.address,
        };

        await patientService.register(payload);
        successCount++;
      } catch (err) {
        failCount++;
        failures.push({
          row: row.index,
          name: `${row.firstName} ${row.lastName}`,
          error: err.response?.data?.error || err.message || 'Server error',
        });
      }

      setImportProgress(Math.round(((i + 1) / validRows.length) * 100));
    }

    setIsImporting(false);
    setImportResult({
      total: validRows.length,
      successCount,
      failCount,
      failures,
    });

    if (successCount > 0 && onImportComplete) {
      onImportComplete(successCount);
    }
  };

  const validCount = parsedRows.filter(r => r.isValid).length;
  const errorCount = parsedRows.length - validCount;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
          color: isDark ? '#F1F5F9' : '#0F172A',
          borderRadius: '10px',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon sx={{ color: '#2563EB', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
            Import Patients from CSV
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={isImporting} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        {/* Step 1: Upload / Template bar */}
        {!importResult && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                sx={{
                  bgcolor: '#2563EB',
                  '&:hover': { bgcolor: '#1D4ED8' },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                Choose CSV File
              </Button>
              {file && (
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600 }}>
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<TemplateIcon sx={{ fontSize: 16 }} />}
              onClick={handleDownloadTemplate}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.72rem',
                color: isDark ? '#CBD5E1' : '#475569',
                borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#CBD5E1',
              }}
            >
              Download Sample Template
            </Button>
          </Box>
        )}

        {parsingError && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.78rem' }}>
            {parsingError}
          </Alert>
        )}

        {/* Import Progress Bar */}
        {isImporting && (
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                Registering patients into PACS database...
              </Typography>
              <Typography variant="caption" fontWeight={700}>
                {importProgress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={importProgress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        )}

        {/* Results Banner */}
        {importResult && (
          <Box sx={{ my: 1 }}>
            <Alert
              severity={importResult.failCount === 0 ? 'success' : importResult.successCount > 0 ? 'warning' : 'error'}
              sx={{ mb: 2, fontSize: '0.8rem' }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
                Import Completed: {importResult.successCount} of {importResult.total} patient(s) successfully registered.
              </Typography>
              {importResult.failCount > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {importResult.failCount} record(s) failed. (See details below)
                </Typography>
              )}
            </Alert>

            {importResult.failures.length > 0 && (
              <Box sx={{ maxHeight: 150, overflowY: 'auto', p: 1, bgcolor: isDark ? 'rgba(239,68,68,0.08)' : '#FEF2F2', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.2)' }}>
                {importResult.failures.map((f, idx) => (
                  <Typography key={idx} variant="caption" display="block" sx={{ color: '#EF4444', fontSize: '0.72rem' }}>
                    Row {f.row} ({f.name}): {f.error}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Preview Table */}
        {parsedRows.length > 0 && !importResult && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#94A3B8' : '#64748B' }}>
                PREVIEW ({parsedRows.length} RECORDS FOUND)
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  icon={<ValidIcon sx={{ fontSize: '13px !important' }} />}
                  label={`${validCount} Ready`}
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                />
                {errorCount > 0 && (
                  <Chip
                    size="small"
                    icon={<ErrorIcon sx={{ fontSize: '13px !important' }} />}
                    label={`${errorCount} Issues`}
                    color="error"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                  />
                )}
              </Stack>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 320, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`, bgcolor: isDark ? '#141414' : '#F8FAFC', borderRadius: '6px' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: isDark ? '#1E1E1E' : '#F1F5F9', py: '6px !important', px: '8px !important', fontSize: '0.72rem', fontWeight: 700 } }}>
                    <TableCell>Status</TableCell>
                    <TableCell>MRN</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Sex</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Phone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedRows.map((row) => (
                    <TableRow key={row.index} sx={{ '& td': { py: '4px !important', px: '8px !important', fontSize: '0.72rem', borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9' } }}>
                      <TableCell>
                        {row.isValid ? (
                          <Tooltip title="Valid record">
                            <ValidIcon sx={{ fontSize: 15, color: '#10B981' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={row.errors.join(', ')}>
                            <ErrorIcon sx={{ fontSize: 15, color: '#EF4444' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{row.mrn || <span style={{ opacity: 0.5 }}>Auto</span>}</TableCell>
                      <TableCell>{row.firstName || '-'}</TableCell>
                      <TableCell>{row.lastName || '-'}</TableCell>
                      <TableCell>{row.gender}</TableCell>
                      <TableCell>{row.birthDate || '-'}</TableCell>
                      <TableCell>{row.phone || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          py: 1.5,
          px: 2.5,
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          justifyContent: 'space-between',
        }}
      >
        <Button
          size="small"
          onClick={handleClose}
          disabled={isImporting}
          sx={{ textTransform: 'none', color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.75rem' }}
        >
          {importResult ? 'Close' : 'Cancel'}
        </Button>

        {!importResult && (
          <Button
            size="small"
            variant="contained"
            disabled={isImporting || validCount === 0}
            onClick={handleImport}
            sx={{
              bgcolor: '#2563EB',
              '&:hover': { bgcolor: '#1D4ED8' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 2,
            }}
          >
            {isImporting ? 'Importing...' : `Import ${validCount} Patient${validCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
